import {
    Stack,
    Radio,
    Divider,
} from '@mantine/core';

import {
    CardElement,
    AddressElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';

import { useEffect, useMemo, useRef, useState } from 'react';
import { PaymentDetailsDto, SubscriptionPlanDto } from 'api/dtos/subscriptionPlanDtos';
import useGetUser from 'hooks/useGetUser';
import { useGetPaymentMethodsQuery, useSubscribeMutation } from 'api/subscriptionPlanEndpoints';
import { Heading, Loading, Modal, Section } from '@carbon/react';
import { defineMessages, useIntl } from 'react-intl';
import sharedMessages from 'Components/utils/sharedMessages';
import styles from './SubscribeModal.module.scss';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';

interface Props {
  opened: boolean;
  onClose: () => void;
  subscriptionPlan: SubscriptionPlanDto;
}

const SubscribeModal = ({
    opened,
    onClose,
    subscriptionPlan,
}: Props) => {
    const intl = useIntl();
    const stripe = useStripe();
    const elements = useElements();
    const user = useGetUser();

    const { data, isFetching, isError, refetch } = useGetPaymentMethodsQuery();

    const paymentMethods = useMemo(() => data ?? [], [data]);

    const hasBeenSetUp = useRef(false);

    const [selectedMethodToken, setSelectedMethodToken] = useState<string>(
        paymentMethods[0]?.paymentToken ?? 'new',
    );

    useEffect(() => {
        if (!hasBeenSetUp.current && data && (data.length === 0 || data.length > 0)) {
            setSelectedMethodToken(paymentMethods[0]?.paymentToken);
            hasBeenSetUp.current = true;
        }
    }, [data, paymentMethods]);

    const [subscribe, { isLoading }] = useSubscribeMutation();

    const [stripeLoading, setStripeLoading] = useState(false);

    if (!user) {
        return null;
    }

    const handleSubscribe = async () => {
        if (!stripe || !elements) return;

        const paymentDetails: PaymentDetailsDto | null = await (async () => {
            if (selectedMethodToken !== 'new') {
                const selectedMethod = paymentMethods.find((method) => method.paymentToken === selectedMethodToken);

                if (!selectedMethod) {
                    return null;
                }

                return selectedMethod;
            } 
            const cardElement = elements.getElement(CardElement);
            const addressElement = elements.getElement(AddressElement);

            if (!cardElement || !addressElement) {
                return null;
            }

            setStripeLoading(true);

            const addressResult = await addressElement.getValue();
            if (!addressResult.complete) {
                return null;
            }

            const result = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: addressResult.value.name,
                    address: addressResult.value.address,
                },
            });

            if (result.error) {
                console.error(result.error);
                return null;
            }

            const pm = result.paymentMethod;
            setStripeLoading(false);

            return {
                paymentToken: pm.id,
                paymentMethodType: pm.type,
                lastFourDigits: pm.card?.last4 ?? '',
                cardBrand: pm.card?.brand ?? '',
                expirationMonth: pm.card?.exp_month ?? 0,
                expirationYear: pm.card?.exp_year.toString() ?? '',
                billingAddress: {
                    fullName: addressResult.value.name,
                    line1: addressResult.value.address.line1,
                    line2: addressResult.value.address.line2,
                    city: addressResult.value.address.city,
                    state: addressResult.value.address.state,
                    postalCode: addressResult.value.address.postal_code,
                    country: addressResult.value.address.country,
                    email: user.email,
                },
                isDefault: true,
            };
        })();

        if (paymentDetails === null) {
            return;
        }


        try {
            await subscribe({
                subscriptionPlanId: subscriptionPlan.id,
                paymentDetails,
            }).unwrap();
        } catch (error: { data: { message: string } } | any) {
            alert(error.data.message);
        }

        onClose();
    };

    return (
        <Modal
            open={opened}
            onRequestClose={onClose}
            loadingStatus={(isLoading || stripeLoading) ? 'active' : undefined}
            primaryButtonDisabled={(isLoading || stripeLoading)}
            title={intl.formatMessage(messages.smallHeader)}
            onRequestSubmit={handleSubscribe}
            modalHeading={intl.formatMessage(messages.selectPaymentMethod)}
            modalLabel={intl.formatMessage(messages.smallHeader)}
            primaryButtonText={intl.formatMessage(sharedMessages.submit)}
            closeButtonLabel={intl.formatMessage(sharedMessages.cancel)}
            secondaryButtonText={intl.formatMessage(sharedMessages.back)}
        >
            <Stack>
                <Heading className={styles.mainHeading}>
                    {intl.formatMessage(messages.selectedPlan)}
                    :
                    {' '}
                    <span className={styles.subscriptionPlanName}>{subscriptionPlan.name}</span>
                </Heading>
                {isFetching && <Loading />}
                {isError && <ErrorActionState onClick={refetch} />}
                {!isError && paymentMethods.length > 0 && (
                    <>
                        <Radio.Group
                            value={selectedMethodToken}
                            onChange={(v) => { setSelectedMethodToken(v); }}
                        >
                            <Stack>
                                {paymentMethods.map((pm) => (
                                    <Radio
                                        key={pm.paymentToken}
                                        value={pm.paymentToken}
                                        label={`${pm.cardBrand.toUpperCase()} •••• ${pm.lastFourDigits} (exp ${pm.expirationMonth}/${pm.expirationYear})`}
                                    />
                                ))}

                                <Radio value="new" label="Dodaj nową kartę" />
                            </Stack>
                        </Radio.Group>
                        <Divider />
                    </>
                )}

                {selectedMethodToken === 'new' && (
                    <Section>
                        <Heading className={styles.heading}>
                            {intl.formatMessage(sharedMessages.card)}
                        </Heading>
                        <CardElement />
                        <Heading className={styles.heading}>
                            {intl.formatMessage(sharedMessages.billingAddress)}
                        </Heading>
                        <AddressElement
                            options={{
                                mode: 'billing',
                                allowedCountries: ['PL'],
                            }}
                        />
                    </Section>
                )}
            </Stack>
        </Modal>
    );
};

const messages = defineMessages({
    smallHeader: {
        id: 'src.Screens.Dashboard.Subscription.SubscribeModal.SubscribeModal.smallHeader',
        defaultMessage: 'Wybór metody płatności',
    },
    selectPaymentMethod: {
        id: 'src.Screens.Dashboard.Subscription.SubscribeModal.SubscribeModal.selectPaymentMethod',
        defaultMessage: 'Wybierz metodę płatności lub dodaj nową',
    },
    selectedPlan: {
        id: 'src.Screens.Dashboard.Subscription.SubscribeModal.SubscribeModal.selectedPlan',
        defaultMessage: 'Wybrany plan',
    },
});

export default SubscribeModal;
